// POST /api/accounts -- the one endpoint that already exists (execution plan
// WS-3 step 6). Kept inside `features/connect/**` rather than `api/hooks/`:
// the frozen `useAccounts.ts` is read-only (GET /api/accounts) and is not
// this workstream's file to extend, and `api/hooks/{...}` for WS-3 is a
// closed, named list that does not include a manual-account mutation. This
// stays inside the paths this workstream owns.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import client from '@/api/client';
import { parseEnvelope } from '@/api/envelope';
import { accountSchema, type AccountKind } from '@/api/hooks/useAccounts';

export interface CreateManualAccountInput {
  display_name: string;
  kind: AccountKind;
  currency?: string;
  business_use?: 'none' | 'mixed' | 'primary';
  opening_balance?: string;
}

export const MANUAL_ACCOUNT_CAPABILITIES = {
  opening_as_of: false,
  operation_id: false,
  versioned_edit: false,
  later_balance: false,
  balance_history: false,
} as const;

export function useCreateManualAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateManualAccountInput) => {
      const body: CreateManualAccountInput = {
        display_name: input.display_name,
        kind: input.kind,
        ...(input.currency !== undefined ? { currency: input.currency } : {}),
        ...(input.business_use !== undefined ? { business_use: input.business_use } : {}),
        ...(input.opening_balance !== undefined ? { opening_balance: input.opening_balance } : {}),
      };
      const response = await client.post('/api/accounts', body);
      return parseEnvelope(accountSchema, response.data).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
